import { Document, Font, Page, PDFDownloadLink, PDFViewer, StyleSheet, Text, View } from '@react-pdf/renderer'
import PropTypes from 'prop-types'
import React from 'react'

import TimesNewRomanBoldItalic from '../../../components/PDFGenerator/fonts/Times New Roman Bold Italic.ttf'
import TimesNewRomanBold from '../../../components/PDFGenerator/fonts/Times New Roman Bold.ttf'
import TimesNewRomanItalic from '../../../components/PDFGenerator/fonts/Times New Roman Italic.ttf'
import TimesNewRoman from '../../../components/PDFGenerator/fonts/Times New Roman.ttf'
import { Cell, Row, Table } from '../../../components/PDFGenerator/Table'
import { safeFormatDate, safeFormatTime } from '../../../functions/safe'
import {
  ACT_ORDER_DATE_KZ,
  ACT_ORDER_DATE_RU,
  ACT_ORDER_NUMBER,
  getFormNumber,
  getOrganizationLines
} from '../actPdfUtils'
import ActPdfFooter from '../ActPdfFooter'

const formatTime = Intl.DateTimeFormat('ru-RU', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
}).format

const formatDate = Intl.DateTimeFormat('ru-RU', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}).format

const styles = StyleSheet.create({
  page: {
    padding: '20 50 10 50'
  },
  titleWorks: {
    fontSize: 12,
    fontWidth: 600,
    fontFamily: 'Times New Roman Bold',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginVertical: 10
  },
  string: {
    fontSize: 10,
    marginVertical: 5
  },
  simpleString: {
    display: 'inline',
    fontSize: '10px'
  },
  table: {
    fontSize: 10,
    fontFamily: 'Times New Roman',
    marginVertical: 5
  },
  sampleBold: {
    fontFamily: 'Times New Roman Bold',
    fontSize: 8
  },
  sampleRegular: {
    fontFamily: 'Times New Roman',
    fontSize: 8
  }
})

Font.register({
  family: 'Times New Roman',
  src: TimesNewRoman,
})
Font.register({
  family: 'Times New Roman Italic',
  src: TimesNewRomanItalic,
})
Font.register({
  family: 'Times New Roman Bold',
  src: TimesNewRomanBold,
})

ActBuyProductsPDFGenerator.propTypes = PDFDocument.propTypes = {
  data: PropTypes.object,
  name: PropTypes.string,
  kazakhName: PropTypes.string,
  formNumber: PropTypes.string,
}

function PDFDocument({ data, name, kazakhName, formNumber }) {
  const actData = data.data
  const resolvedFormNumber = getFormNumber(data, formNumber) || data?.number || ''
  const organizationLines = getOrganizationLines(data)
  const schema = [
    {
      key: '1. Объектінің атауы (Наименование объекта)',
      value: actData?.object?.value?.name
    },
    {
      key: '2. Тексеру белгіленгені туралы актінің нөмірі мен күні (Номер и дата акта о назначении контрольного закупа)',
      value: actData?.numberandDate?.value
    },
    {
      key: '3. Сатып алынатын өнімнің (тауардың) атауы, олардың саны және өндірушісі (Наименование закупаемой продукции (товара), их количество и производитель)',
      value: actData?.productName?.value
    },
    {
      key: '4. Сатып алуды жүргізу күні мен уақыты (Дата и время проведения покупки)',
      value: actData?.selectionDateTime?.value ? safeFormatTime(actData?.selectionDateTime?.value) : ' '
    },
    {
      key: '5. Санитариялық-эпидemiологиялық зерттеу өткізу үшін санитариялық-эпidemiологиялық мекемеге жеткізілген күні мен уақыты (Дата и время доставки в организацию санитарно-эпidemiологической службы для проведения санитарно-эпidemiологической экспертизы)',
      value: actData?.deliveryDateTime?.value ? safeFormatTime(actData?.deliveryDateTime?.value) : ' '
    },
    {
      key: '6. Өнімдерді тасымалдау барысында сақтау шарттары (Условия хранения продукции при транспортировки)',
      value: actData?.transportationCondition?.value?.name
    },
    {
      key: '7. Пломбалау кезінде қаптаманың түрі (вид упаковки при опломбировании) (полимерлі, қағаз, шыны, басқалар) (полимерное, бумага, стекло, другое)',
      value: actData?.sealTypeAndNumber?.value
    },
    {
      key: '8. Жарамдылық мерзімі көрсетілмеген және (немесе) өтіп кеткен және айқын бұзылу белгілері бар өнімдер (тауарлар) туралы ақпарат (Информация о продукции (товарах) с неустановленным и (или) истекшим сроком годности, явными признаками порчи)',
      value: actData?.outdatedProduction?.value
    },
    {
      key: '9. Қабылданған жедел ден қою шаралары туралы белгі (Отметка о принятых мерах оперативного реагирования)',
      value: actData?.reactionOperations?.value
    },
    {
      key: '10. Қосымша мәліметтер (Дополнительные сведения)',
      value: actData?.additionalInfo?.value
    },
    {
      key: '11. Осы актіге қоса беріледі (К настоящему акту прилагаются) бақылау-касса машинасының чегі немесе тауар чегі, тауарларға ілеспе құжат (чек контрольно-кассовой машины или товарный чек, сопроводительная накладная на товары)',
      value: actData?.bill?.value
    },
    {
      key: '12. Сатушы (Продавец)лауазымы, тегі, аты,әкесінің аты (бар болса), қолы (подпись, должность, фамилия, имя,отчество (при его наличии)',
      value: actData?.seller?.value
    },
    {
      key: '13. Лауазымды тұлға (Должностное лицо) лауазымы, тегі, аты, әкесінің аты (бар болса), қолы (подпись, должность, фамилия, имя,отчество (при его наличии)',
      value: actData?.responsible?.value
    },
    {
      key: '14. Сатушы өнімді (тауарды) сатып алу актісіне қол қоюдан бас тартты (Продавец отказался от подписания акта покупки продукции (товара))лауазымды адамның Тегі, Аты, Әкесінің аты (бар болса) және қолы (подпись и фамилия, имя, отчество (при его наличии) должностного лица)',
      value: actData?.rejected?.value
    },
  ]

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <Table style={styles.table}>
          <Row>
            <Cell></Cell>
            <Cell>
              <Text>Нысанның БҚСЖ бойынша коды</Text>
              <Text>Код формы по ОКУД ___________________</Text>
              <Text>КҰЖЖ бойынша ұйым коды</Text>
              <Text>Код организации по ОКПО _____________</Text>
            </Cell>
          </Row>
          <Row>
            <Cell>
              <Text>Қазақстан Республикасының Денсаулық сақтау министрлігі Министерство здравоохранения Республики Казахстан</Text>
            </Cell>
            <Cell>
              <Text>Қазақстан Республикасының Денсаулық сақтау министрінің {ACT_ORDER_DATE_KZ} {ACT_ORDER_NUMBER} бұйрығымен бекітілген № {resolvedFormNumber}/е нысанды медициналық құжаттама</Text>
            </Cell>
          </Row>
          <Row>
            <Cell>
              <Text>Санитариялық-эпидемиологиялық қызметтiң мемлекеттiк органының атауы Наименование государственного органа санитарно-эпидемиологической службы</Text>
              {organizationLines.length
                ? organizationLines.map((line, index) => (
                  <Text key={index}>{line}</Text>
                ))
                : <Text> </Text>}
            </Cell>
            <Cell>
              <Text>Медицинская документация Форма № {resolvedFormNumber}/у Утверждена приказом Министра здравоохранения Республики Казахстан от {ACT_ORDER_DATE_RU} {ACT_ORDER_NUMBER}</Text>
            </Cell>
          </Row>
        </Table>
        <View style={styles.titleWorks} wrap>
          <Text>{kazakhName}</Text>
          <Text>{name}</Text>
          <Text>№{data.number} от {actData?.dateOfAct?.value ? safeFormatDate(actData.dateOfAct.value) : ' '}</Text>
        </View>
        <Table style={styles.table}>
          {schema.map(el => {
            return <Row>
              <Cell>
                <Text style={{ fontFamily: 'Times New Roman Bold' }}>
                  {el.key}:
                </Text>
              </Cell>
              <Cell>
                <Text>
                  {el.value || ' '}
                </Text>
              </Cell>
            </Row>
          })}
        </Table>

        {actData?.samples?.value.map(el => {
          return <Table style={styles.table}>
            <Row>
              <Cell>
                <Text style={styles.sampleBold}>
                  Тiркелу нөмірi (Регистрационный номер):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.registrationNumber?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Үлгілер өнімдерінің (тауардың) атауы, өндірушісі (наименования образцов продукции (товара), производитель):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.sample?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Алынған үлгілер саны (Количество образцов, ед.):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.quantity?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Сынаманың салмағы, көлемі (Вес, объем образца):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.weightSize?.value || ' '}
                </Text>
              </Cell>
            </Row>
            <Row>
              <Cell>
                <Text style={styles.sampleBold}>
                  Сақтау мерзімі (Cрок годности):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.expirationDate?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Бірлік үшін бағасы, теңге (Цена за единицу, тенге):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.priceOne?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Жалпы сомасы, теңге (Итого сумма, тенге):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.summary?.value || ' '}
                </Text>
              </Cell>
              <Cell>
                <Text style={styles.sampleBold}>
                  Пломба нөмірі (Номер пломбы):
                </Text>
                <Text style={styles.sampleRegular}>
                  {el.plombNumber?.value || ' '}
                </Text>
              </Cell>
            </Row>
          </Table>
        })}

        <ActPdfFooter data={data} styles={styles} />
      </Page>
    </Document>
  )
}

export default function ActBuyProductsPDFGenerator(props) {
  return (
    <>
      <PDFViewer style={{ width: '100%', height: 700 }}>
        <PDFDocument {...props} />
      </PDFViewer>
      <PDFDownloadLink document={<PDFDocument {...props} />} fileName={`${formatTime(new Date())}.pdf`}>
        {({ url }) => url ? 'Скачать PDF' : 'Загрузка...'}
      </PDFDownloadLink>
    </>
  )
}
